import { IAntDProTableRequest } from "../../../general/type/general.types";
import { apiRequest, downloadFile } from "../../../services/api";
import {
  IClaimRequest,
  IApiResponseGetClaims,
  IUpdateClaimStatusRequest,
} from "../type/claim";

export const getClaims = async (
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetClaims> => {
  const response = await apiRequest("GET", "/claims", tableRequest);
  return response.data;
};

export const getUserClaims = async (
  userId: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetClaims> => {
  const response = await apiRequest(
    "GET",
    `/claims/user-claims/${userId}`,
    tableRequest
  );
  return response.data;
};

export const createClaim = async (data: IClaimRequest, files?: File[]) => {
  if (files?.length) {
    const formData = new FormData();

    // Append files
    files.forEach((file) => formData.append("file", file));

    // Append claim data
    formData.append("claimType", data.claimType);
    data.startDate && formData.append("startDate", String(data.startDate));
    data.endDate && formData.append("endDate", String(data.endDate));
    data.totalDays && formData.append("totalDays", data.totalDays.toString());
    data.claimAmount &&
      formData.append("claimAmount", data.claimAmount.toString());
    data.claimDetails && formData.append("claimDetails", data.claimDetails);

    const response = await apiRequest("POST", `/claims`, formData, true);
    return response.data;
  }

  // No files - use JSON payload
  const payload = {
    claimType: data.claimType,
    ...(data.startDate && { startDate: data.startDate }),
    ...(data.endDate && { endDate: data.endDate }),
    ...(data.totalDays && { totalDays: data.totalDays }),
    ...(data.claimAmount && { claimAmount: data.claimAmount }),
    ...(data.claimDetails && { claimDetails: data.claimDetails }),
  };

  const response = await apiRequest<IClaimRequest>("POST", "/claims", payload);
  return response.data;
};

export const getClaimById = async (id: string | undefined) => {
  const response = await apiRequest<IApiResponseGetClaims>(
    "GET",
    `/claims/${id}`
  );
  return response.data;
};

export const editClaim = async (
  id: string,
  data: IClaimRequest,
  files?: File[],
  existingFiles?: string[]
) => {
  // If existingFiles is provided, user modified files - use FormData
  const hasFileChanges = existingFiles !== undefined;

  if (hasFileChanges) {
    const formData = new FormData();

    // Append new files
    files?.forEach((file) => formData.append("file", file));

    // Append existing files to keep (reusing existing fileUrl field)
    if (existingFiles.length > 0) {
      existingFiles.forEach((url) => formData.append("fileUrl", url));
    } else {
      // Empty string signals all files removed
      formData.append("fileUrl", "");
    }

    // Append claim data
    formData.append("claimType", data.claimType);
    data.startDate && formData.append("startDate", String(data.startDate));
    data.endDate && formData.append("endDate", String(data.endDate));
    data.totalDays && formData.append("totalDays", data.totalDays.toString());
    data.claimAmount &&
      formData.append("claimAmount", data.claimAmount.toString());
    data.claimDetails && formData.append("claimDetails", data.claimDetails);

    const response = await apiRequest("PATCH", `/claims/${id}`, formData, true);
    return response.data;
  }

  const payload = {
    ...(data.claimType && { claimType: data.claimType }),
    ...(data.startDate && { startDate: data.startDate }),
    ...(data.endDate && { endDate: data.endDate }),
    ...(data.totalDays && { totalDays: data.totalDays }),
    ...(data.claimAmount && { claimAmount: data.claimAmount }),
    ...(data.claimDetails && { claimDetails: data.claimDetails }),
  };

  const response = await apiRequest<IClaimRequest>(
    "PATCH",
    `/claims/${id}`,
    payload
  );
  return response.data;
};

export const updateClaimStatus = async (
  id: string,
  data: IUpdateClaimStatusRequest
) => {
  const payload = {
    status: data.status,
  };
  const response = await apiRequest<IUpdateClaimStatusRequest>(
    "PATCH",
    `/claims/${id}/status`,
    payload
  );
  return response.data;
};

export const archiveClaim = async (id: string) => {
  const response = await apiRequest("PATCH", `/claims/${id}/archive`);
  return response.data;
};

export const downloadClaimFileFromS3 = async (
  claimId: string,
  relativePath: string
) => {
  try {
    // Use the standardized downloadFile function to get the blob
    const blob = await downloadFile(
      `/claims/${claimId}/download?filePath=${encodeURIComponent(relativePath)}`
    );

    // Get the filename from the relative path
    const fileName = relativePath.split("/").pop() || "downloaded-file";

    // Create download URL
    const downloadUrl = window.URL.createObjectURL(blob);

    // *** Try to open in new tab for supported file types (PDF, images) *** //
    const isPdf = blob.type === "application/pdf";
    const isImage = blob.type.startsWith("image/");

    if (isPdf || isImage) {
      // Open in new tab for supported file types
      const newTab = window.open(downloadUrl, "_blank");

      // Fallback to download if opening in new tab fails
      if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
        // Browser blocked opening new tab, fall back to download
        triggerDownload(downloadUrl, fileName);
      } else {
        // Revoke the blob URL when the parent page is closed/refreshed
        // This prevents the blob URL from being reused after closing the parent tab
        window.addEventListener(
          "beforeunload",
          () => {
            window.URL.revokeObjectURL(downloadUrl);
          },
          { once: true }
        );
      }
    } else {
      // For other file types, use regular download
      triggerDownload(downloadUrl, fileName);
    }
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

// Helper function to trigger file download
const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, 100);
};
